import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { buildTheme, DEFAULT_PRIMARY, DEFAULT_ACCENT } from '../lib/themeUtils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Turnstile } from '@marsidev/react-turnstile';

const STEPS = [
  { label: 'Στοιχεία', shortLabel: 'Στοιχεία' },
  { label: 'Ραντεβού', shortLabel: 'Ραντεβού' },
  { label: 'Επιβεβαίωση', shortLabel: 'Επιβεβαίωση' },
];

const PET_TYPES = [
  { value: 'dog', label: 'Σκύλος', icon: '🐕' },
  { value: 'cat', label: 'Γάτα', icon: '🐈' },
  { value: 'other', label: 'Άλλο', icon: '🐾' },
];

const MONTHS_NOMINATIVE = [
  'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος',
  'Μάιος', 'Ιούνιος', 'Ιούλιος', 'Αύγουστος',
  'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'
];

const DAYS_SHORT = ['Κυρ', 'Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ'];

// Custom Calendar Button for the DatePicker fallback
const CustomCalendarInput = React.forwardRef(({ value, onClick, theme }, ref) => (
  <button
    type="button"
    onClick={onClick}
    ref={ref}
    className="p-3 rounded-xl border-2 transition-all hover:scale-105 shadow-sm flex items-center justify-center"
    style={{ borderColor: theme.cardBorder, backgroundColor: theme.unselectedBg, color: theme.textSecondary }}
    title="Επιλογή από ημερολόγιο"
  >
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  </button>
));

// ─── Progress Indicator ───────────────────────────────────────────
const ProgressIndicator = ({ currentStep, theme }) => {
  const progressPercent = Math.round((currentStep / STEPS.length) * 100);

  return (
    <div
      className="sticky top-0 z-40 px-4 pt-5 pb-4 backdrop-blur-md"
      style={{ backgroundColor: `${theme.bg}F2` }} // F2 for slight transparency if hex
    >
      {/* Step circles + labels */}
      <div className="flex items-center justify-between max-w-sm mx-auto mb-4 relative z-10">
        {STEPS.map((step, idx) => {
          const stepNum = idx + 1;
          const isCompleted = currentStep > stepNum;
          const isCurrent = currentStep === stepNum;
          const isFuture = currentStep < stepNum;

          return (
            <React.Fragment key={stepNum}>
              <div className="flex flex-col items-center bg-transparent">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500 ease-out"
                  style={{
                    background: isCompleted || isCurrent
                      ? theme.gradient
                      : theme.inactiveCircle,
                    color: isFuture ? theme.textSecondary : '#FFFFFF',
                    boxShadow: isCurrent ? `0 0 16px ${theme.primaryGlow}` : 'none',
                    transform: isCurrent ? 'scale(1.1)' : 'scale(1)'
                  }}
                >
                  {isCompleted ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    stepNum
                  )}
                </div>
                <span
                  className="text-xs mt-2 font-bold uppercase tracking-wider transition-colors duration-300"
                  style={{ color: isFuture ? theme.textSecondary : isCurrent ? theme.primaryText : theme.textSecondary }}
                >
                  {step.shortLabel}
                </span>
              </div>

              {/* Connector line */}
              {idx < STEPS.length - 1 && (
                <div
                  className="flex-1 h-1 mx-3 rounded-full transition-all duration-500 ease-out -mt-6"
                  style={{
                    background: currentStep > stepNum
                      ? theme.gradientHorizontal
                      : theme.cardBorder,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ─── Trust Badges ────────────────────────────────────────────────
const TrustBadges = ({ theme }) => {
  const badges = [
    {
      icon: (
        <svg className="w-5 h-5" style={{ color: theme.primaryText }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      ),
      label: 'Άμεση Επιβεβαίωση',
    },
    {
      icon: (
        <svg className="w-5 h-5" style={{ color: theme.primaryText }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: 'Έγκαιρη Υπενθύμιση',
    },
    {
      icon: (
        <svg className="w-5 h-5" style={{ color: theme.primaryText }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      label: 'Ασφαλής Κράτηση',
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-3 mb-8">
      {badges.map((badge, i) => (
        <div
          key={i}
          className="flex flex-col items-center gap-2 py-4 px-2 rounded-2xl text-center transition-all hover:-translate-y-1"
          style={{
            background: theme.primaryUltraLight,
          }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm"
            style={{ background: theme.primaryMedium }}
          >
            {badge.icon}
          </div>
          <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
            {badge.label}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────
const PublicBookingPage = () => {
  const { slug } = useParams();

  // Clinic data
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [canShowCustomPage, setCanShowCustomPage] = useState(false);

  // Scheduling data
  const [clinicSettings, setClinicSettings] = useState(null);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [shiftAssignments, setShiftAssignments] = useState([]);
  const [shiftHourAssignments, setShiftHourAssignments] = useState([]);
  const [availableVets, setAvailableVets] = useState([]);
  const [exceptionDays, setExceptionDays] = useState([]);
  const [specialDays, setSpecialDays] = useState([]);
  const [existingAppointments, setExistingAppointments] = useState([]);

  // Client info (Step 1)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [petName, setPetName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [petType, setPetType] = useState('');
  const [petTypeOther, setPetTypeOther] = useState('');

  // Booking form (Step 2)
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedVet, setSelectedVet] = useState('');
  const [notes, setNotes] = useState('');

  // Step 3
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [stepErrors, setStepErrors] = useState({});

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const toastTimeoutRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const dateStripRef = useRef(null);

  // Dynamic theme
  const effectiveThemeId = canShowCustomPage ? (clinic?.theme_id || 'light') : 'dark';
  const theme = useMemo(() => buildTheme(
    canShowCustomPage ? (clinic?.primary_color || DEFAULT_PRIMARY) : DEFAULT_PRIMARY,
    canShowCustomPage ? (clinic?.accent_color || DEFAULT_ACCENT) : DEFAULT_ACCENT,
    effectiveThemeId
  ), [clinic?.primary_color, clinic?.accent_color, canShowCustomPage, effectiveThemeId]);

  // Strip Dates Calculation
  const stripDates = useMemo(() => {
    const dates = [];
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    
    const maxMonths = clinicSettings?.max_booking_months || 3;
    const end = new Date();
    end.setMonth(end.getMonth() + maxMonths);
    end.setHours(0, 0, 0, 0);

    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, [clinicSettings?.max_booking_months]);

  // ─── Effects ──────────────────────────────────────────────────

  useEffect(() => {
    loadClinicBySlug();
  }, [slug]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (clinic) {
      document.title = `${clinic.name} - Online Κράτηση`;
      loadClinicData();
    }
  }, [clinic]);

  useEffect(() => {
    if (clinic && selectedDate) {
      loadAvailableShifts();
    }
  }, [clinic, selectedDate]);

  // Auto-scroll Date Strip to selected date
  useEffect(() => {
    if (wizardStep === 2 && dateStripRef.current && selectedDate) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const el = document.getElementById(`date-btn-${dateStr}`);
      if (el) {
        // Use a small timeout to ensure rendering is complete before scrolling
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 50);
      }
    }
  }, [selectedDate, wizardStep]);

  // Reset cascade: date changes → reset vet + time
  useEffect(() => {
    setSelectedVet('');
    setSelectedTime('');
  }, [selectedDate]);

  // Reset cascade: vet changes → reset time
  useEffect(() => {
    setSelectedTime('');
  }, [selectedVet]);

  // ─── Data Loading ─────────────────────────────────────────────

  const loadClinicBySlug = async () => {
    try {
      const { data, error } = await supabase
        .from('clinics')
        .select('id, name, city, address, street_number, mobile_phone, line_phone, logo_url, primary_color, accent_color, public_intro_text, theme_id')
        .eq('slug', slug)
        .single();

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setClinic(data);

      const { data: subData } = await supabase
        .from('clinic_subscriptions')
        .select('plan_id, subscription_status(name)')
        .eq('clinic_id', data.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const planId = subData?.plan_id;
      const statusName = subData?.subscription_status?.name;
      if (
        planId && ['free', 'premium', 'professional', 'clinic max'].includes(planId.toLowerCase()) &&
        statusName && ['active', 'trial', 'expiring_soon'].includes(statusName)
      ) {
        setCanShowCustomPage(true);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading clinic:', err);
      setNotFound(true);
      setLoading(false);
    }
  };

  const loadClinicData = async () => {
    try {
      const { data: settingsData, error: settingsError } = await supabase
        .from('settings_clinic_settings')
        .select('*')
        .eq('clinic_id', clinic.id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        console.error('Error loading clinic settings:', settingsError);
      } else if (settingsData) {
        setClinicSettings(settingsData);
      }

      const [exceptionResult, specialResult] = await Promise.all([
        supabase
          .from('settings_clinic_exception_days')
          .select('*')
          .eq('clinic_id', clinic.id),
        supabase
          .from('settings_clinic_special_days')
          .select('*')
          .eq('clinic_id', clinic.id)
      ]);

      if (exceptionResult.data) setExceptionDays(exceptionResult.data);
      if (specialResult.data) setSpecialDays(specialResult.data);
    } catch (error) {
      console.error('Error loading clinic data:', error);
    }
  };

  const loadAvailableShifts = async () => {
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      const jsDay = selectedDate.getDay();
      const dayOfWeek = jsDay === 0 ? 7 : jsDay;

      const { data: shiftsData, error: shiftsError } = await supabase
        .from('shifts')
        .select(`
          *,
          shift_day_groups (
            *,
            shift_days (
              weekday_id,
              settings_weekdays (*)
            ),
            shift_hours (*)
          )
        `)
        .eq('clinic_id', clinic.id)
        .eq('is_active', true)
        .lte('from_date', dateString)
        .or(`to_date.gte.${dateString},is_infinite.eq.true`);

      if (shiftsError) {
        console.error('Error loading shifts:', shiftsError);
        return;
      }

      const filteredShifts = shiftsData?.filter(shift => {
        return shift.shift_day_groups?.some(group =>
          group.shift_days?.some(day => day.weekday_id === dayOfWeek)
        );
      }) || [];

      setAvailableShifts(filteredShifts);

      if (filteredShifts.length > 0) {
        const shiftIds = filteredShifts.map(shift => shift.id);

        const [assignmentsResult, hourAssignmentsResult] = await Promise.all([
          supabase
            .from('shift_assignments')
            .select('shift_id, vet_id')
            .in('shift_id', shiftIds),
          supabase
            .from('shift_hour_assignments')
            .select('shift_id, hour_id, vet_id, weekday_id')
            .in('shift_id', shiftIds)
        ]);

        if (assignmentsResult.error) {
          console.error('Error loading shift assignments:', assignmentsResult.error);
          return;
        }
        if (hourAssignmentsResult.error) {
          console.error('Error loading hour assignments:', hourAssignmentsResult.error);
          return;
        }

        setShiftAssignments(assignmentsResult.data || []);
        setShiftHourAssignments(hourAssignmentsResult.data || []);

        const shiftVetIds = (assignmentsResult.data || []).map(a => a.vet_id);
        const hourVetIds = (hourAssignmentsResult.data || []).map(a => a.vet_id);
        const vetIds = [...new Set([...shiftVetIds, ...hourVetIds])];

        if (vetIds.length > 0) {
          const { data: vetsData, error: vetsError } = await supabase
            .from('vets')
            .select('*')
            .in('id', vetIds)
            .eq('active', true)
            .eq('deleted', false);

          if (vetsError) {
            console.error('Error loading vets:', vetsError);
          } else {
            setAvailableVets(vetsData || []);
          }
        } else {
          setAvailableVets([]);
        }
      } else {
        setShiftAssignments([]);
        setShiftHourAssignments([]);
        setAvailableVets([]);
      }

      const { data: appointmentsData } = await supabase
        .from('appointments')
        .select('appointment_date, duration_minutes, vet_id')
        .eq('clinic_id', clinic.id)
        .gte('appointment_date', `${dateString}T00:00:00`)
        .lte('appointment_date', `${dateString}T23:59:59`);

      setExistingAppointments(appointmentsData || []);
    } catch (error) {
      console.error('Error loading available shifts:', error);
    }
  };

  // ─── Scheduling Logic ─────────────────────────────────────────

  const getVetsForSelectedDate = () => {
    if (!availableShifts.length) return [];

    const jsDay = selectedDate.getDay();
    const dayOfWeek = jsDay === 0 ? 7 : jsDay;
    const workingVetIds = new Set();

    availableShifts.forEach(shift => {
      shiftAssignments.forEach(assignment => {
        if (assignment.shift_id === shift.id) {
          workingVetIds.add(assignment.vet_id);
        }
      });

      shiftHourAssignments.forEach(assignment => {
        if (assignment.shift_id === shift.id && assignment.weekday_id === dayOfWeek) {
          workingVetIds.add(assignment.vet_id);
        }
      });
    });

    return availableVets.filter(vet => workingVetIds.has(vet.id));
  };

  const generateTimeSlotsForVet = (vetId) => {
    if (!clinicSettings || !availableShifts.length) return [];

    const jsDay = selectedDate.getDay();
    const dayOfWeek = jsDay === 0 ? 7 : jsDay;
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const appointmentDuration = clinicSettings.appointment_duration_minutes || 30;

    const isClosedException = exceptionDays.some(ex => {
      if (!ex.is_closed) return false;
      if (ex.is_yearly) {
        const exDate = new Date(ex.exception_date);
        return exDate.getMonth() === selectedDate.getMonth() && exDate.getDate() === selectedDate.getDate();
      }
      return ex.exception_date === selectedDateStr;
    });

    if (isClosedException) return [];

    const matchingSpecialDay = specialDays.find(sp => {
      if (sp.is_yearly) {
        const spDate = new Date(sp.special_date);
        return spDate.getMonth() === selectedDate.getMonth() && spDate.getDate() === selectedDate.getDate();
      }
      return sp.special_date === selectedDateStr;
    });

    const slots = [];

    if (matchingSpecialDay) {
      const startTime = new Date(`2000-01-01T${matchingSpecialDay.start_time}`);
      const endTime = new Date(`2000-01-01T${matchingSpecialDay.end_time}`);
      let currentTime = new Date(startTime);

      while (currentTime < endTime) {
        slots.push({ time: currentTime.toTimeString().slice(0, 5) });
        currentTime.setMinutes(currentTime.getMinutes() + appointmentDuration);
      }
    } else {
      availableShifts.forEach(shift => {
        const hasShiftLevel = shiftAssignments.some(a =>
          a.shift_id === shift.id && a.vet_id === vetId
        );

        if (hasShiftLevel) {
          shift.shift_day_groups?.forEach(group => {
            const hasDay = group.shift_days?.some(d => d.weekday_id === dayOfWeek);
            if (!hasDay) return;

            group.shift_hours?.forEach(hour => {
              const startTime = new Date(`2000-01-01T${hour.start_time}`);
              const endTime = new Date(`2000-01-01T${hour.end_time}`);
              let currentTime = new Date(startTime);

              while (currentTime < endTime) {
                const timeString = currentTime.toTimeString().slice(0, 5);
                if (!slots.find(s => s.time === timeString)) {
                  slots.push({ time: timeString });
                }
                currentTime.setMinutes(currentTime.getMinutes() + appointmentDuration);
              }
            });
          });
        } else {
          const vetHourAssignments = shiftHourAssignments.filter(a =>
            a.shift_id === shift.id && a.vet_id === vetId && a.weekday_id === dayOfWeek
          );

          vetHourAssignments.forEach(assignment => {
            shift.shift_day_groups?.forEach(group => {
              const matchingHour = group.shift_hours?.find(h => h.id === assignment.hour_id);
              if (matchingHour) {
                const startTime = new Date(`2000-01-01T${matchingHour.start_time}`);
                const endTime = new Date(`2000-01-01T${matchingHour.end_time}`);
                let currentTime = new Date(startTime);

                while (currentTime < endTime) {
                  const timeString = currentTime.toTimeString().slice(0, 5);
                  if (!slots.find(s => s.time === timeString)) {
                    slots.push({ time: timeString });
                  }
                  currentTime.setMinutes(currentTime.getMinutes() + appointmentDuration);
                }
              }
            });
          });
        }
      });
    }

    const bookedTimes = new Set();
    existingAppointments.forEach(apt => {
      if (apt.vet_id !== vetId) return;
      const aptDate = new Date(apt.appointment_date);
      const aptTime = aptDate.toTimeString().slice(0, 5);
      const aptEnd = new Date(aptDate);
      aptEnd.setMinutes(aptEnd.getMinutes() + (apt.duration_minutes || appointmentDuration));
      const aptEndTime = aptEnd.toTimeString().slice(0, 5);

      slots.forEach(slot => {
        if (slot.time >= aptTime && slot.time < aptEndTime) {
          bookedTimes.add(slot.time);
        }
      });
    });

    const isToday = selectedDate.toDateString() === new Date().toDateString();
    const now = new Date();
    const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    return slots
      .filter(s => !bookedTimes.has(s.time))
      .filter(s => !isToday || s.time > currentTimeStr)
      .sort((a, b) => a.time.localeCompare(b.time));
  };

  const generateTimeSlotsForAny = () => {
    const vetsForDate = getVetsForSelectedDate();
    const slotMap = new Map();

    vetsForDate.forEach(vet => {
      const vetSlots = generateTimeSlotsForVet(vet.id);
      vetSlots.forEach(slot => {
        if (!slotMap.has(slot.time)) {
          slotMap.set(slot.time, []);
        }
        slotMap.get(slot.time).push(vet.id);
      });
    });

    return [...slotMap.entries()]
      .map(([time, vetIds]) => ({ time, availableVetIds: vetIds }))
      .sort((a, b) => a.time.localeCompare(b.time));
  };

  const resolveVetForTime = (time) => {
    const vetsForDate = getVetsForSelectedDate();
    for (const vet of vetsForDate) {
      const vetSlots = generateTimeSlotsForVet(vet.id);
      if (vetSlots.some(s => s.time === time)) {
        return vet.id;
      }
    }
    return null;
  };

  // ─── Validation ───────────────────────────────────────────────

  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      if (!firstName.trim()) errors.firstName = 'Το όνομα είναι υποχρεωτικό';
      if (!lastName.trim()) errors.lastName = 'Το επώνυμο είναι υποχρεωτικό';
      if (!petName.trim()) errors.petName = 'Το όνομα κατοικιδίου είναι υποχρεωτικό';
      if (!phone.trim()) errors.phone = 'Το τηλέφωνο είναι υποχρεωτικό';
      if (!email.trim()) errors.email = 'Το email είναι υποχρεωτικό';
      if (!petType) errors.petType = 'Επιλέξτε τύπο κατοικιδίου';
      if (petType === 'other' && !petTypeOther.trim()) errors.petTypeOther = 'Συμπληρώστε τον τύπο';
    }

    if (step === 2) {
      if (!selectedVet) errors.selectedVet = 'Επιλέξτε κτηνίατρο';
      if (!selectedTime) errors.selectedTime = 'Επιλέξτε ώρα';
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(wizardStep)) return;
    setWizardStep(prev => Math.min(prev + 1, 3));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStepErrors({});
    setWizardStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ─── Booking ──────────────────────────────────────────────────

  const bookAppointment = async () => {
    if (!acceptTerms) {
      displayToast('Παρακαλώ αποδεχτείτε τους Όρους Χρήσης', 'error');
      return;
    }

    if (!turnstileToken) {
      displayToast('Παρακαλώ περιμένετε την επαλήθευση ασφαλείας.', 'error');
      return;
    }

    const actualVetId = selectedVet === 'any' ? resolveVetForTime(selectedTime) : selectedVet;
    if (!actualVetId) {
      displayToast('Δεν βρέθηκε διαθέσιμος κτηνίατρος για αυτή την ώρα', 'error');
      return;
    }

    setSubmitting(true);

    try {
      // Check banned contacts
      const { data: bannedContacts } = await supabase
        .from('settings_banned_contacts')
        .select('id')
        .eq('clinic_id', clinic.id)
        .or(`email.eq.${email.trim()},phone.eq.${phone.trim()}`)
        .limit(1);

      if (bannedContacts && bannedContacts.length > 0) {
        displayToast('Δεν είναι δυνατή η κράτηση. Παρακαλώ επικοινωνήστε με την κλινική.', 'error');
        setSubmitting(false);
        return;
      }

      // Check plan appointment limit
      const { data: limitData, error: limitError } = await supabase.rpc('check_clinic_limit', {
        p_clinic_id: clinic.id,
        p_limit_type: 'appointments'
      });

      if (limitError) {
        console.warn('Appointment limit check failed:', limitError);
      } else if (limitData && !limitData.allowed) {
        displayToast('Η κλινική δεν δέχεται άλλα ραντεβού online. Παρακαλώ επικοινωνήστε τηλεφωνικά.', 'error');
        setSubmitting(false);
        return;
      }

      const appointmentDateTime = new Date(selectedDate);
      const [hours, minutes] = selectedTime.split(':');
      appointmentDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const resolvedPetType = petType === 'other' ? petTypeOther.trim() : petType;

      const { error } = await supabase
        .from('appointments')
        .insert({
          appointment_date: appointmentDateTime.toISOString(),
          duration_minutes: clinicSettings?.appointment_duration_minutes || 30,
          notes: notes.trim() || null,
          vet_id: actualVetId,
          clinic_id: clinic.id,
          client_first_name: firstName.trim(),
          client_last_name: lastName.trim(),
          client_pet_name: petName.trim(),
          client_phone: phone.trim(),
          client_email: email.trim(),
          client_pet_type: resolvedPetType,
          status: clinicSettings?.requires_confirmation ? 'scheduled' : 'confirmed'
        });

      if (error) {
        console.error('Error booking appointment:', error);
        displayToast('Σφάλμα κατά την κράτηση του ραντεβού', 'error');
        setSubmitting(false);
        return;
      }

      setBookingSuccess(true);
    } catch (error) {
      console.error('Error booking appointment:', error);
      displayToast('Σφάλμα κατά την κράτηση', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setPetName('');
    setPhone('');
    setEmail('');
    setPetType('');
    setPetTypeOther('');
    setSelectedTime('');
    setSelectedVet('');
    setNotes('');
    setAcceptTerms(false);
    setBookingSuccess(false);
    setWizardStep(1);
    setStepErrors({});
    setTurnstileToken('');
    turnstileRef.current?.reset();
    loadAvailableShifts();
  };

  const displayToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    toastTimeoutRef.current = setTimeout(() => setShowToast(false), 4000);
  };

  // ─── Computed ─────────────────────────────────────────────────

  const vetsForDate = getVetsForSelectedDate();
  const timeSlotsForVet = selectedVet
    ? (selectedVet === 'any' ? generateTimeSlotsForAny() : generateTimeSlotsForVet(selectedVet))
    : [];

  const getSelectedVetName = () => {
    if (selectedVet === 'any') return 'Οποιοσδήποτε Διαθέσιμος';
    const vet = availableVets.find(v => v.id === selectedVet);
    return vet ? `${vet.first_name} ${vet.last_name}` : '';
  };

  const formatDate = (date) => {
    const days = ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'];
    const months = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαΐ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const getPetTypeLabel = () => {
    if (petType === 'other') return petTypeOther;
    const found = PET_TYPES.find(p => p.value === petType);
    return found ? `${found.icon} ${found.label}` : '';
  };

  // ─── Inline error helper ─────────────────────────────────────

  const FieldError = ({ field }) => {
    if (!stepErrors[field]) return null;
    return (
      <p className="text-[11px] font-bold mt-1.5 px-1 uppercase tracking-wide" style={{ color: theme.errorColor }}>
        * {stepErrors[field]}
      </p>
    );
  };

  // ─── Loading / Not Found / Success states ─────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: theme.primary }}></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}>
        <div className="text-center p-8">
          <svg className="mx-auto h-20 w-20 mb-4" style={{ color: theme.textSecondary }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h2 className="text-2xl font-bold mb-2" style={{ color: theme.textPrimary }}>Κλινική δεν βρέθηκε</h2>
          <p style={{ color: theme.textSecondary }}>Ο σύνδεσμος που ακολουθήσατε δεν αντιστοιχεί σε κάποια ενεργή κλινική.</p>
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden" style={{ backgroundColor: theme.bg }}>
        {/* Success Confetti/Glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/10 pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-[100px] pointer-events-none opacity-20" style={{ background: theme.primary }}></div>
        
        <div className="text-center p-8 max-w-md bg-white/5 backdrop-blur-xl rounded-3xl border shadow-2xl relative z-10" style={{ borderColor: theme.cardBorder }}>
          <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" style={{ background: theme.gradient }}>
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-extrabold mb-3" style={{ color: theme.textPrimary }}>Επιτυχής Κράτηση!</h2>
          <p className="mb-8 leading-relaxed" style={{ color: theme.textSecondary }}>
            Το ραντεβού σας καταχωρήθηκε επιτυχώς στο σύστημα. 
            {clinicSettings?.requires_confirmation ? ' Η κλινική θα επικοινωνήσει μαζί σας σύντομα για την τελική επιβεβαίωση.' : ' Σας περιμένουμε στην κλινική μας την επιλεγμένη ώρα.'}
          </p>
          <button
            onClick={resetForm}
            className="w-full px-6 py-4 rounded-xl font-bold text-white transition-all shadow-lg hover:scale-[1.02]"
            style={{ background: theme.gradient, boxShadow: `0 8px 20px ${theme.primaryShadow}` }}
          >
            Κλείσιμο νέου ραντεβού
          </button>
        </div>
      </div>
    );
  }

  // ─── Step Content ─────────────────────────────────────────────

  const renderStep1 = () => (
    <div className="rounded-2xl p-6 border shadow-lg" style={{ background: theme.cardBgStyle, borderColor: theme.cardBorder }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl shadow-sm" style={{ backgroundColor: theme.primaryUltraLight }}>
          <svg className="w-6 h-6" style={{ color: theme.primaryText }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold" style={{ color: theme.textPrimary }}>Στοιχεία Επικοινωνίας</h3>
          <p className="text-sm" style={{ color: theme.textSecondary }}>Συμπληρώστε τα στοιχεία σας και του κατοικιδίου</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: theme.textSecondary }}>Όνομα *</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="π.χ. Μαρία"
            className="w-full px-4 py-3.5 rounded-xl border text-base focus:outline-none transition-all focus:ring-2"
            style={{
              backgroundColor: theme.inputBg,
              borderColor: stepErrors.firstName ? theme.errorColor : theme.inputBorder,
              color: theme.inputText || theme.textPrimary,
            }}
          />
          <FieldError field="firstName" />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: theme.textSecondary }}>Επώνυμο *</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="π.χ. Παπαδοπούλου"
            className="w-full px-4 py-3.5 rounded-xl border text-base focus:outline-none transition-all focus:ring-2"
            style={{
              backgroundColor: theme.inputBg,
              borderColor: stepErrors.lastName ? theme.errorColor : theme.inputBorder,
              color: theme.inputText || theme.textPrimary,
            }}
          />
          <FieldError field="lastName" />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: theme.textSecondary }}>Όνομα Κατοικιδίου *</label>
          <input
            type="text"
            value={petName}
            onChange={(e) => setPetName(e.target.value)}
            placeholder="π.χ. Rex"
            className="w-full px-4 py-3.5 rounded-xl border text-base focus:outline-none transition-all focus:ring-2"
            style={{
              backgroundColor: theme.inputBg,
              borderColor: stepErrors.petName ? theme.errorColor : theme.inputBorder,
              color: theme.inputText || theme.textPrimary,
            }}
          />
          <FieldError field="petName" />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: theme.textSecondary }}>Τηλέφωνο *</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="π.χ. 697 1234567"
            className="w-full px-4 py-3.5 rounded-xl border text-base focus:outline-none transition-all focus:ring-2"
            style={{
              backgroundColor: theme.inputBg,
              borderColor: stepErrors.phone ? theme.errorColor : theme.inputBorder,
              color: theme.inputText || theme.textPrimary,
            }}
          />
          <FieldError field="phone" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-semibold mb-2" style={{ color: theme.textSecondary }}>Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="π.χ. maria@example.com"
            className="w-full px-4 py-3.5 rounded-xl border text-base focus:outline-none transition-all focus:ring-2"
            style={{
              backgroundColor: theme.inputBg,
              borderColor: stepErrors.email ? theme.errorColor : theme.inputBorder,
              color: theme.inputText || theme.textPrimary,
            }}
          />
          <FieldError field="email" />
        </div>

        {/* Pet Type Selector */}
        <div className="sm:col-span-2 pt-2">
          <label className="block text-sm font-semibold mb-3" style={{ color: theme.textSecondary }}>Τύπος Κατοικιδίου *</label>
          <div className="grid grid-cols-3 gap-3">
            {PET_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => {
                  setPetType(type.value);
                  if (type.value !== 'other') setPetTypeOther('');
                }}
                className={`flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-2xl border-2 transition-all duration-300 ${petType === type.value ? 'scale-105' : 'hover:scale-[1.02]'}`}
                style={{
                  borderColor: petType === type.value ? theme.primary : theme.inputBorder,
                  background: petType === type.value
                    ? theme.gradientSelection
                    : theme.unselectedBg,
                  boxShadow: petType === type.value ? `0 8px 20px ${theme.primaryShadowLight}` : 'none',
                }}
              >
                <span className="text-3xl mb-1">{type.icon}</span>
                <span className="text-sm font-bold tracking-wide uppercase" style={{ color: petType === type.value ? theme.primaryText : theme.textSecondary }}>
                  {type.label}
                </span>
              </button>
            ))}
          </div>
          <FieldError field="petType" />

          {/* Other pet type text input */}
          {petType === 'other' && (
            <div className="mt-4 animate-fade-in">
              <input
                type="text"
                value={petTypeOther}
                onChange={(e) => setPetTypeOther(e.target.value)}
                placeholder="π.χ. Κουνέλι, Ιγκουάνα..."
                className="w-full px-4 py-3.5 rounded-xl border text-base focus:outline-none transition-all focus:ring-2"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: stepErrors.petTypeOther ? theme.errorColor : theme.primary,
                  color: theme.inputText || theme.textPrimary,
                  boxShadow: `0 0 0 1px ${theme.primary}`
                }}
              />
              <FieldError field="petTypeOther" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const maxMonths = clinicSettings?.max_booking_months || 3;
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + maxMonths);

    return (
      <div className="space-y-5">
        {/* Horizontal Date Selection */}
        <div className="rounded-2xl p-6 border shadow-lg" style={{ background: theme.cardBgStyle, borderColor: theme.cardBorder }}>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.primaryUltraLight }}>
                <svg className="w-6 h-6" style={{ color: theme.primaryText }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold capitalize" style={{ color: theme.textPrimary }}>
                  {MONTHS_NOMINATIVE[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                </h3>
                <p className="text-sm" style={{ color: theme.textSecondary }}>Επιλέξτε ημέρα</p>
              </div>
            </div>
            
            {/* Fallback DatePicker Button */}
            <DatePicker
              selected={selectedDate}
              onChange={(date) => date && setSelectedDate(date)}
              customInput={<CustomCalendarInput theme={theme} />}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              maxDate={maxDate}
              calendarClassName="dark-datepicker-modern"
              popperPlacement="bottom-end"
            />
          </div>
          
          <div 
            ref={dateStripRef}
            className="flex overflow-x-auto gap-3 pb-4 pt-1 px-1 custom-scrollbar snap-x scroll-smooth"
          >
            {stripDates.map((d) => {
              const isSelected = d.toDateString() === selectedDate.toDateString();
              const isToday = d.toDateString() === new Date().toDateString();
              
              return (
                <button
                  key={d.toISOString()}
                  id={`date-btn-${d.toISOString().split('T')[0]}`}
                  onClick={() => setSelectedDate(d)}
                  className={`snap-center flex-shrink-0 flex flex-col items-center justify-center w-[72px] h-[90px] rounded-2xl border-2 transition-all duration-300 relative ${isSelected ? 'scale-105 shadow-lg' : 'hover:scale-[1.02]'}`}
                  style={{
                    borderColor: isSelected ? theme.primary : theme.inputBorder,
                    background: isSelected ? theme.gradient : theme.unselectedBg,
                    color: isSelected ? '#FFFFFF' : theme.textPrimary,
                    boxShadow: isSelected ? `0 8px 20px ${theme.primaryShadowLight}` : 'none'
                  }}
                >
                  <span className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ opacity: isSelected ? 0.9 : 0.6 }}>
                    {DAYS_SHORT[d.getDay()]}
                  </span>
                  <span className="text-2xl font-black">
                    {d.getDate()}
                  </span>
                  {isToday && !isSelected && (
                    <div className="absolute bottom-2 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.primary }}></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Vet Selection */}
        <div className="rounded-2xl p-6 border shadow-lg" style={{ background: theme.cardBgStyle, borderColor: stepErrors.selectedVet ? theme.errorColor : theme.cardBorder }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.primaryUltraLight }}>
              <svg className="w-6 h-6" style={{ color: theme.primaryText }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold" style={{ color: theme.textPrimary }}>Ιατρός</h3>
              <p className="text-sm" style={{ color: theme.textSecondary }}>Ποιος θέλετε να σας αναλάβει;</p>
            </div>
          </div>

          {vetsForDate.length > 0 ? (
            <div className="flex items-stretch gap-3 overflow-x-auto pb-4 -mb-4 custom-scrollbar px-1 pt-1">
              {/* "Any Available" option */}
              <div
                onClick={() => setSelectedVet('any')}
                className={`flex-shrink-0 cursor-pointer transition-all duration-300 rounded-2xl p-5 min-w-[120px] flex flex-col items-center justify-center border-2 ${selectedVet === 'any' ? 'scale-105' : 'hover:scale-[1.02]'}`}
                style={{
                  borderColor: selectedVet === 'any' ? theme.primary : theme.inputBorder,
                  background: selectedVet === 'any'
                    ? theme.gradientSelection
                    : theme.unselectedBg,
                  boxShadow: selectedVet === 'any' ? `0 8px 20px ${theme.primaryShadowLight}` : 'none',
                }}
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 shadow-md" style={{ background: theme.gradient }}>
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-sm font-bold text-center uppercase tracking-wide" style={{ color: selectedVet === 'any' ? theme.primaryText : theme.textSecondary }}>
                  Οποιοσδήποτε
                </p>
              </div>

              {/* Individual vet cards */}
              {vetsForDate.map((vet) => (
                <div
                  key={vet.id}
                  onClick={() => setSelectedVet(vet.id)}
                  className={`flex-shrink-0 cursor-pointer transition-all duration-300 rounded-2xl p-5 min-w-[120px] flex flex-col items-center justify-center border-2 ${selectedVet === vet.id ? 'scale-105' : 'hover:scale-[1.02]'}`}
                  style={{
                    borderColor: selectedVet === vet.id ? theme.primary : theme.inputBorder,
                    background: selectedVet === vet.id
                      ? theme.gradientSelection
                      : theme.unselectedBg,
                    boxShadow: selectedVet === vet.id ? `0 8px 20px ${theme.primaryShadowLight}` : 'none',
                  }}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white mb-3 shadow-md"
                    style={{ background: theme.gradient }}
                  >
                    {vet.first_name?.[0]}{vet.last_name?.[0]}
                  </div>
                  <p className="text-sm font-bold text-center max-w-[90px] truncate" style={{ color: selectedVet === vet.id ? theme.primaryText : theme.textSecondary }}>
                    {vet.first_name}
                  </p>
                  <p className="text-xs font-medium text-center max-w-[90px] truncate mt-0.5" style={{ color: selectedVet === vet.id ? theme.primaryText : theme.textSecondary, opacity: selectedVet === vet.id ? 0.8 : 1 }}>
                    {vet.last_name}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-black/5 rounded-xl border border-dashed border-gray-500/30">
              <p className="font-medium" style={{ color: theme.textSecondary }}>Δεν εργάζονται κτηνίατροι αυτήν την ημέρα</p>
              <p className="text-sm mt-1 opacity-70" style={{ color: theme.textSecondary }}>Παρακαλώ επιλέξτε διαφορετική ημερομηνία</p>
            </div>
          )}
          <FieldError field="selectedVet" />
        </div>

        {/* Time Slots */}
        {selectedVet && (
          <div className="rounded-2xl p-6 border shadow-lg animate-fade-in" style={{ background: theme.cardBgStyle, borderColor: stepErrors.selectedTime ? theme.errorColor : theme.cardBorder }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.primaryUltraLight }}>
                <svg className="w-6 h-6" style={{ color: theme.primaryText }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold" style={{ color: theme.textPrimary }}>Διαθέσιμες Ώρες</h3>
                <p className="text-sm" style={{ color: theme.textSecondary }}>Επιλέξτε την ώρα που σας εξυπηρετεί</p>
              </div>
            </div>
            {timeSlotsForVet.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {timeSlotsForVet.map((slot, index) => {
                  const isSelected = selectedTime === slot.time;
                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedTime(slot.time)}
                      className={`px-3 py-3 rounded-xl text-base font-bold transition-all duration-300 border-2 ${isSelected ? 'scale-105 shadow-md' : 'hover:scale-[1.02]'}`}
                      style={{
                        color: isSelected ? '#FFFFFF' : theme.textSecondary,
                        borderColor: isSelected ? theme.primary : theme.inputBorder,
                        background: isSelected
                          ? theme.gradient
                          : theme.unselectedBg,
                        boxShadow: isSelected ? `0 4px 12px ${theme.primaryShadowLight}` : 'none'
                      }}
                    >
                      {slot.time}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 bg-black/5 rounded-xl border border-dashed border-gray-500/30">
                <p className="font-medium" style={{ color: theme.textSecondary }}>Δεν υπάρχουν διαθέσιμες ώρες</p>
              </div>
            )}
            <FieldError field="selectedTime" />
          </div>
        )}

        {/* Notes */}
        <div className="rounded-2xl p-6 border shadow-lg" style={{ background: theme.cardBgStyle, borderColor: theme.cardBorder }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 rounded-xl" style={{ backgroundColor: theme.primaryUltraLight }}>
              <svg className="w-6 h-6" style={{ color: theme.primaryText }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold" style={{ color: theme.textPrimary }}>Σημειώσεις</h3>
              <span className="text-xs" style={{ color: theme.textSecondary }}>(προαιρετικά)</span>
            </div>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="π.χ. Το κατοικίδιο μου φοβάται τα άλλα σκυλιά..."
            rows="4"
            maxLength={500}
            className="w-full px-5 py-4 rounded-xl border text-base resize-none focus:outline-none focus:ring-2 transition-all"
            style={{ backgroundColor: theme.inputBg, borderColor: theme.inputBorder, color: theme.inputText || theme.textPrimary, '--tw-ring-color': theme.primary }}
          />
          <p className="text-xs font-medium mt-2 text-right opacity-70" style={{ color: theme.textSecondary }}>{notes.length}/500</p>
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-6 animate-fade-in">
      {/* Summary Card */}
      <div className="rounded-3xl p-6 md:p-8 border shadow-2xl relative overflow-hidden" style={{ background: theme.cardBgStyle, borderColor: theme.cardBorder }}>
        {/* Ticket Header styling */}
        <div className="absolute top-0 left-0 w-full h-2" style={{ background: theme.gradientHorizontal }}></div>
        
        <div className="text-center mb-8 pt-2">
          <h3 className="text-2xl font-extrabold tracking-tight" style={{ color: theme.textPrimary }}>Σύνοψη Ραντεβού</h3>
          <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>Ελέγξτε τα στοιχεία πριν την επιβεβαίωση</p>
        </div>

        <div className="space-y-0 relative">
          
          {/* Date & Time (Hero) */}
          <div className="flex flex-col items-center justify-center p-6 bg-black/5 rounded-2xl mb-6 border border-black/5">
            <p className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: theme.primaryText }}>{formatDate(selectedDate)}</p>
            <p className="text-5xl font-black tracking-tight" style={{ color: theme.textPrimary }}>{selectedTime}</p>
          </div>

          {/* Divider */}
          <div className="w-full border-t border-dashed my-6" style={{ borderColor: theme.cardBorder }}></div>

          {/* Client Name */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <span className="text-xl opacity-60">👤</span>
              <p className="text-sm font-medium" style={{ color: theme.textSecondary }}>Πελάτης</p>
            </div>
            <p className="text-base font-bold text-right" style={{ color: theme.textPrimary }}>{firstName} {lastName}</p>
          </div>

          {/* Pet Name + Type */}
          <div className="flex items-center justify-between py-3 border-t" style={{ borderColor: theme.cardBorder }}>
            <div className="flex items-center gap-3">
              <span className="text-xl opacity-60">🐾</span>
              <p className="text-sm font-medium" style={{ color: theme.textSecondary }}>Κατοικίδιο</p>
            </div>
            <p className="text-base font-bold text-right" style={{ color: theme.textPrimary }}>{petName} <span className="font-normal opacity-70">({getPetTypeLabel()})</span></p>
          </div>

          {/* Vet */}
          <div className="flex items-center justify-between py-3 border-t" style={{ borderColor: theme.cardBorder }}>
            <div className="flex items-center gap-3">
              <span className="text-xl opacity-60">👨‍⚕️</span>
              <p className="text-sm font-medium" style={{ color: theme.textSecondary }}>Κτηνίατρος</p>
            </div>
            <p className="text-base font-bold text-right" style={{ color: theme.primaryText }}>{getSelectedVetName()}</p>
          </div>

          {/* Clinic */}
          <div className="flex items-center justify-between py-3 border-t" style={{ borderColor: theme.cardBorder }}>
            <div className="flex items-center gap-3">
              <span className="text-xl opacity-60">🏥</span>
              <p className="text-sm font-medium" style={{ color: theme.textSecondary }}>Κλινική</p>
            </div>
            <div className="text-right">
              <p className="text-base font-bold" style={{ color: theme.textPrimary }}>{clinic.name}</p>
              {(clinic.address || clinic.city) && (
                <p className="text-xs mt-0.5 opacity-70" style={{ color: theme.textSecondary }}>
                  {[clinic.address, clinic.street_number, clinic.city].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </div>

          {/* Notes if present */}
          {notes.trim() && (
            <div className="mt-4 p-4 rounded-xl bg-black/5 border border-black/5">
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: theme.textSecondary }}>Σημειωσεις</p>
              <p className="text-sm italic leading-relaxed" style={{ color: theme.textPrimary }}>"{notes}"</p>
            </div>
          )}
        </div>
      </div>

      {/* Terms Checkbox */}
      <div
        className="rounded-xl p-5 border transition-all hover:shadow-md cursor-pointer group"
        style={{
          background: theme.cardBgStyle,
          borderColor: theme.cardBorder,
        }}
        onClick={() => setAcceptTerms(!acceptTerms)}
      >
        <div className="flex items-start gap-4">
          <div className="relative flex items-center justify-center mt-0.5">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptTerms}
              readOnly
              className="peer appearance-none w-6 h-6 border-2 rounded-lg transition-all"
              style={{ 
                borderColor: acceptTerms ? theme.primary : theme.inputBorder,
                backgroundColor: acceptTerms ? theme.primary : 'transparent'
              }}
            />
            <svg className="absolute w-4 h-4 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <label
            className="text-sm leading-relaxed cursor-pointer select-none"
            style={{ color: theme.textSecondary }}
          >
            Επιβεβαιώνω ότι τα στοιχεία είναι σωστά και αποδέχομαι τους{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold underline underline-offset-2 hover:opacity-80 transition-opacity"
              style={{ color: theme.primaryText }}
              onClick={(e) => e.stopPropagation()}
            >
              Όρους Χρήσης
            </a>
            {' '}και την{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold underline underline-offset-2 hover:opacity-80 transition-opacity"
              style={{ color: theme.primaryText }}
              onClick={(e) => e.stopPropagation()}
            >
              Πολιτική Απορρήτου
            </a>
            .
          </label>
        </div>
      </div>

      <div className="flex justify-center mt-4">
        <Turnstile
          ref={turnstileRef}
          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
          onSuccess={setTurnstileToken}
          onError={() => setTurnstileToken('')}
          onExpire={() => setTurnstileToken('')}
          theme={effectiveThemeId === 'dark' ? 'dark' : 'light'}
        />
      </div>
    </div>
  );

  // ─── Navigation Buttons ───────────────────────────────────────

  const renderNavButtons = () => {
    if (wizardStep === 1) {
      return (
        <button
          onClick={handleNext}
          className="w-full px-6 py-4 rounded-xl font-bold text-white transition-all duration-300 text-base flex items-center justify-center gap-2 hover:scale-[1.02]"
          style={{
            background: theme.gradientHorizontal,
            boxShadow: `0 8px 20px ${theme.primaryShadow}`,
          }}
        >
          Επόμενο Βήμα
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      );
    }

    if (wizardStep === 2) {
      return (
        <div className="flex gap-3">
          <button
            onClick={handleBack}
            className="flex-[1] px-4 py-4 rounded-xl font-bold transition-all duration-300 text-sm border hover:bg-black/5"
            style={{
              color: theme.textSecondary,
              borderColor: theme.inputBorder,
              backgroundColor: 'transparent',
            }}
          >
            Πίσω
          </button>
          <button
            onClick={handleNext}
            className="flex-[2.5] px-6 py-4 rounded-xl font-bold text-white transition-all duration-300 text-base flex items-center justify-center gap-2 hover:scale-[1.02]"
            style={{
              background: theme.gradientHorizontal,
              boxShadow: `0 8px 20px ${theme.primaryShadow}`,
            }}
          >
            Σύνοψη
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      );
    }

    if (wizardStep === 3) {
      return (
        <div className="flex gap-3">
          <button
            onClick={handleBack}
            disabled={submitting}
            className="flex-[1] px-4 py-4 rounded-xl font-bold transition-all duration-300 text-sm border disabled:opacity-50 hover:bg-black/5"
            style={{
              color: theme.textSecondary,
              borderColor: theme.inputBorder,
              backgroundColor: 'transparent',
            }}
          >
            Πίσω
          </button>
          <button
            onClick={bookAppointment}
            disabled={!acceptTerms || !turnstileToken || submitting}
            className="flex-[2.5] px-6 py-4 rounded-xl font-bold text-white transition-all duration-300 text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 disabled:shadow-none hover:scale-[1.02]"
            style={{
              background: !acceptTerms || !turnstileToken || submitting
                ? theme.inputBorder
                : theme.gradientHorizontal,
              boxShadow: !acceptTerms || !turnstileToken || submitting ? 'none' : `0 8px 20px ${theme.primaryShadow}`,
            }}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                 <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                 Κράτηση...
              </span>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                Επιβεβαίωση
              </>
            )}
          </button>
        </div>
      );
    }
  };

  // ─── Main Render ──────────────────────────────────────────────

  return (
    <div className="min-h-screen pb-32 sm:pb-12" style={{ backgroundColor: theme.bg, color: theme.textPrimary, position: 'relative', overflow: 'hidden' }}>
      {/* Background ambient glow */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: `radial-gradient(800px 800px at 10% -10%, ${theme.primaryMedium}, transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)` }} className="pointer-events-none" />

      {/* Toast */}
      {showToast && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-50 animate-fade-in">
          <div
            className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300 shadow-2xl"
            style={{
              background: theme.overlayBg,
              backdropFilter: 'blur(10px)',
              borderColor: toastType === 'error' ? theme.errorColor : theme.primary,
            }}
          >
            <div className="flex-shrink-0 mr-3">
              {toastType === 'error' ? (
                <div className="p-1.5 rounded-lg bg-red-500/20">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </div>
              ) : (
                <div className="p-1.5 rounded-lg" style={{ background: theme.primaryUltraLight }}>
                  <svg className="w-5 h-5" style={{ color: theme.primaryText }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
              )}
            </div>
            <span className="font-medium flex-1 text-sm" style={{ color: theme.textPrimary }}>{toastMessage}</span>
            <button onClick={() => setShowToast(false)} className="ml-3 transition-colors hover:opacity-70" style={{ color: theme.textSecondary }}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <ProgressIndicator currentStep={wizardStep} theme={theme} />

      <div className="relative max-w-2xl mx-auto px-4 py-6 z-10">
        
        {/* Clinic Header */}
        <div className="text-center mb-8">
          {canShowCustomPage && clinic.logo_url ? (
            <div className="inline-block p-1 rounded-full bg-white shadow-xl mb-4">
              <img
                src={clinic.logo_url}
                alt={`${clinic.name} logo`}
                className="w-20 h-20 rounded-full object-cover border"
                style={{ borderColor: theme.cardBorder }}
              />
            </div>
          ) : (
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-extrabold text-white shadow-xl"
              style={{ background: theme.gradientHorizontal }}
            >
              {clinic.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-3" style={{ color: theme.textPrimary }}>{clinic.name}</h1>
          {canShowCustomPage && clinic.public_intro_text && (
            <p className="text-sm lg:text-base mb-3 max-w-md mx-auto leading-relaxed" style={{ color: theme.textSecondary }}>{clinic.public_intro_text}</p>
          )}
          <div className="inline-flex flex-col items-center gap-1.5 px-4 py-2 rounded-xl bg-black/5 border border-black/5 mt-2">
            {(clinic.address || clinic.city) && (
              <p className="text-xs font-medium flex items-center gap-1.5" style={{ color: theme.textSecondary }}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                {[clinic.address, clinic.street_number, clinic.city].filter(Boolean).join(', ')}
              </p>
            )}
            {(clinic.mobile_phone || clinic.line_phone) && (
              <p className="text-xs font-medium flex items-center gap-1.5" style={{ color: theme.textSecondary }}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                {clinic.mobile_phone || clinic.line_phone}
              </p>
            )}
          </div>
        </div>

        {/* Trust Badges */}
        <TrustBadges theme={theme} />

        {/* Step Content Wrapper with transition */}
        <div className="space-y-4 min-h-[400px]">
          {wizardStep === 1 && <div className="animate-fade-in">{renderStep1()}</div>}
          {wizardStep === 2 && <div className="animate-fade-in">{renderStep2()}</div>}
          {wizardStep === 3 && <div className="animate-fade-in">{renderStep3()}</div>}

          {/* Desktop navigation buttons (visible on sm+) */}
          <div className="hidden sm:block pt-6">
            {renderNavButtons()}
          </div>
        </div>

        {/* Powered by TailClinic (hidden for Custom pages) */}
        {!canShowCustomPage && (
          <div className="mt-12 mb-4">
            <div className="border-t" style={{ borderColor: theme.cardBorder }} />
            <p className="text-center mt-6 text-xs font-medium uppercase tracking-widest" style={{ color: theme.textSecondary }}>
              Powered by{' '}
              <a
                href="https://tailclinic.gr"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-80 font-bold"
                style={{ color: theme.primaryText }}
              >
                TailClinic.gr
              </a>
            </p>
          </div>
        )}
      </div>

      {/* Sticky bottom navigation for mobile */}
      <div
        className="fixed bottom-0 left-0 right-0 sm:hidden z-40 px-4 py-4 border-t"
        style={{
          background: `${theme.bg}F2`,
          backdropFilter: 'blur(12px)',
          borderColor: theme.cardBorder,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
        }}
      >
        {renderNavButtons()}
      </div>
    </div>
  );
};

export default PublicBookingPage;